import type { CSSProperties } from "react";

export interface BrandedSchool {
  schoolName: string;
  division?: string | null;
  city?: string | null;
  state?: string | null;
  logoUrl?: string | null;
  brandColorPrimary?: string | null;
  brandColorSecondary?: string | null;
}

export function formatDivision(division?: string | null): string {
  if (!division) return "College golf";
  return division
    .replace("NCAA Division III", "NCAA DIII")
    .replace("NCAA Division II", "NCAA DII")
    .replace("NCAA Division I", "NCAA DI")
    .replace("NJCAA Division III", "NJCAA III")
    .replace("NJCAA Division II", "NJCAA II")
    .replace("NJCAA Division I", "NJCAA I");
}

export function schoolLocation(school: Pick<BrandedSchool, "city" | "state">): string {
  return [school.city, school.state].filter(Boolean).join(", ");
}

export function schoolInitials(name: string): string {
  const ignored = new Set(["of", "the", "at", "and"]);
  const words = name
    .replace(/[–—-]/g, " ")
    .split(/\s+/)
    .filter(word => word && !ignored.has(word.toLowerCase()));
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .slice(0, 3)
    .map(word => word[0])
    .join("")
    .toUpperCase();
}

function validHex(value?: string | null): string | null {
  const normalized = value?.trim();
  if (!normalized || !/^#[0-9a-f]{6}$/i.test(normalized)) return null;
  return normalized.toUpperCase();
}

function rgb(hex: string): [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

function relativeLuminance(hex: string): number {
  const values = rgb(hex).map(value => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return values[0] * 0.2126 + values[1] * 0.7152 + values[2] * 0.0722;
}

function darken(hex: string, amount = 0.32): string {
  const values = rgb(hex).map(value => Math.max(0, Math.round(value * (1 - amount))));
  return `#${values.map(value => value.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

export type BrandStyle = CSSProperties & {
  "--school-primary": string;
  "--school-secondary": string;
  "--school-ink": string;
};

function contrast(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function mix(hex: string, target: string, amount: number): string {
  const from = rgb(hex);
  const to = rgb(target);
  const values = from.map((value, i) => Math.round(value + (to[i] - value) * amount));
  return `#${values.map(value => value.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/** Shift `hex` toward `away` in small steps until it keeps 4.5:1 against `ink`. */
function readableAgainst(hex: string, ink: string): string {
  const away = ink === "#FFFFFF" ? "#000000" : "#FFFFFF";
  let color = hex;
  for (let step = 1; step <= 10 && contrast(color, ink) < 4.5; step++) {
    color = mix(hex, away, step * 0.1);
  }
  return color;
}

/**
 * The header gradient runs primary → secondary and its text sits on both ends.
 * Ink is whichever of white or forest reads better on the primary. If either end
 * can't hold 4.5:1 against that ink, it is deepened (or lightened) in its own
 * hue; a secondary that is far off (e.g. a dark primary paired with white) is
 * replaced with a deeper shade of the primary so the header stays on-brand.
 */
export function schoolBrandStyle(school: BrandedSchool): BrandStyle {
  const rawPrimary = validHex(school.brandColorPrimary) || "#173127";
  const ink =
    contrast(rawPrimary, "#FFFFFF") >= contrast(rawPrimary, "#17231D") ? "#FFFFFF" : "#17231D";
  const primary = readableAgainst(rawPrimary, ink);
  const rawSecondary = validHex(school.brandColorSecondary);
  const secondary =
    rawSecondary && contrast(rawSecondary, ink) >= 3
      ? readableAgainst(rawSecondary, ink)
      : readableAgainst(ink === "#FFFFFF" ? darken(primary) : mix(primary, "#FFFFFF", 0.35), ink);
  return {
    "--school-primary": primary,
    "--school-secondary": secondary,
    "--school-ink": ink,
  };
}
